import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api/client';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import CryptoPaymentModal from '../components/ui/CryptoPaymentModal';
import CardPaymentModal from '../components/ui/CardPaymentModal';

const FEATURES = [
  {
    category: 'Dashboard',
    items: [
      'Real-time player stats: name, map, level, HP/Rage bars, coordinates',
      'Nearby monster list with distance, HP%, and current target indicator',
      'Built-in log panel',
    ],
  },
  {
    category: 'Combat',
    items: [
      'Attack Speed — adjustable attack speed override (slider 2.0–21.0)',
      'Auto Combat — automated skill execution',
      'Fixed slots: left-click, right-click, Slot 2',
      'Unlimited additional numeric skill slots',
      'Skill modes: rotation-based or always-on (for deployable/persistent skills)',
      'Per-skill cooldown tracking',
      'Blocked target detection — automatically skips unreachable enemies',
    ],
  },
  {
    category: 'Autopilot',
    items: [
      'Waypoint navigation with a visual map editor',
      'Waypoint types: move, clear area, kill specific target, open chest, enter gate, use teleport item',
      'Route Groups — chain multiple routes into a single automated run with automatic zone transitions',
      'Stuck detection and recovery',
      'Waypoint recording: automatic or manual',
      'Automatic difficulty selection when entering dungeons',
      'Automatic confirm dialog handling',
    ],
  },
  {
    category: 'Auto Loot',
    items: [
      'Direct Pickup — automatically picks up items within a configurable radius',
      'Pickup Filter — per-item filter editor with search and class-based filtering — supports all languages available in the game',
      'Option to always pick up unidentified equipment',
      'Quick gem and rune tier presets',
      'Auto-Sell — automatically sells items based on a filter, with session stats (gold earned, items sold) — supports all languages available in the game',
    ],
  },
  {
    category: 'Overlay',
    items: [
      'In-game overlay toggleable with a hotkey',
      'Floating minimap with player, monster, and waypoint markers',
    ],
  },
];

const CATEGORY_ICONS = {
  Dashboard: <><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></>,
  Combat:    <><path d="M14.5 3.5l6 6-9 9-3 1 1-3 9-9-2-2-9 9-1 3" /><path d="M3 21l3-3" /></>,
  Autopilot: <><circle cx="12" cy="10" r="3" /><path d="M12 21s-7-6.2-7-11a7 7 0 1114 0c0 4.8-7 11-7 11z" /></>,
  'Auto Loot': <><path d="M6 3h12l4 6-10 12L2 9z" /><path d="M2 9h20" /></>,
  Overlay:   <><rect x="2.5" y="4" width="19" height="14" rx="2" /><path d="M8 21h8M12 18v3" /></>,
};

const PAYMENT_METHODS = [
  { id: 'crypto', label: 'Crypto', hint: 'Base price', icon: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9 8h4.5a2.5 2.5 0 010 5H9v3h4.5a2.5 2.5 0 010 5" />
      <path d="M12 6v2M12 16v2" />
    </svg>
  )},
  { id: 'card', label: 'Card', hint: '+ fee', icon: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20" />
    </svg>
  )},
];

const INCLUDED = [
  'Instant HWID lock activation',
  'Time-based license management',
  'Dedicated support',
];

function CheckIcon({ className = 'w-4 h-4' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
         strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function WarningBox({ children, tone = 'red' }) {
  const tones = {
    red:    'bg-red-500/10 border-red-500/25 text-red-300',
    yellow: 'bg-amber-500/10 border-amber-500/25 text-amber-300',
  };
  return (
    <div className={`px-4 py-3 rounded-lg border text-sm flex items-start gap-2.5 ${tones[tone]}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
           strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 flex-shrink-0 mt-0.5">
        <path d="M12 9v4M12 17h.01" />
        <path d="M10.3 3.86l-8.18 14a1.5 1.5 0 001.3 2.14h16.16a1.5 1.5 0 001.3-2.14l-8.18-14a1.5 1.5 0 00-2.6 0z" />
      </svg>
      <span className="leading-relaxed">{children}</span>
    </div>
  );
}

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [siteStatus, setSiteStatus] = useState('working');
  const [openFeature, setOpenFeature] = useState('Autopilot');
  const [paymentMethod, setPaymentMethod] = useState('crypto');
  const [referralCode, setReferralCode] = useState('');
  const [referralState, setReferralState] = useState(null); // { valid, text }
  const [referralChecking, setReferralChecking] = useState(false);
  const [cryptoOrder, setCryptoOrder] = useState(null);
  const [cardOrder, setCardOrder] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    api.get(`/products/${id}`)
      .then(res => {
        setProduct(res.data);
        setSiteStatus(res.data.siteStatus);
        if (res.data.plans?.length > 0) setSelectedPlan(res.data.plans[0]);
      })
      .catch(() => setError('Product not found.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <LoadingSpinner fullPage label="Loading product…" />;
  if (error) return (
    <div className="page-container text-center">
      <div className="max-w-sm mx-auto card p-8">
        <p className="text-red-300 mb-5">{error}</p>
        <button onClick={() => navigate('/')} className="btn-secondary">Go Back</button>
      </div>
    </div>
  );

  const isUpdating = siteStatus === 'updating';

  /**
   * Jár-e ajánlói jutalom a kiválasztott csomagra?
   *
   * A döntést a SZERVER küldi csomagonként (`referralEligible`) — a szabály
   * (jutalomnál hosszabb csomag) egy helyen, a backendben van kimondva. A `??`
   * csak a régebbi válaszalakra véd: ott minden csomag jogosult volt.
   */
  const referralAllowed = selectedPlan?.referralEligible ?? true;

  /**
   * Csomagváltás.
   *
   * Ha az új csomagra nem jár jutalom, a beírt kódot is ELDOBJUK. Enélkül a mező
   * eltűnne, a már „érvényes"-re ellenőrzött kód viszont bent maradna az
   * állapotban, és a vásárlás egy olyan hibára futna, amit a felhasználó nem is
   * tudna javítani — a beviteli mező ott már nem látszik.
   */
  const selectPlan = (plan) => {
    setSelectedPlan(plan);
    if (!(plan?.referralEligible ?? true)) {
      setReferralCode('');
      setReferralState(null);
    }
  };

  // Vásárlás előtti ellenőrzés, hogy a hibát ne a fizetésnél kapja meg.
  const checkReferral = async () => {
    const code = referralCode.trim();
    if (!code) { setReferralState(null); return; }

    setReferralChecking(true);
    try {
      // A csomagot is elküldjük: a szerver így a csomag-feltételt is elbírálja,
      // nem csak a kódot.
      const { data } = await api.get('/referral/check', {
        params: { code, ...(selectedPlan ? { planId: selectedPlan.id } : {}) },
      });
      setReferralState({
        valid: true,
        text: `Valid — ${data.referrer} gets +${data.rewardDays} days when this order is paid.`,
      });
    } catch (err) {
      setReferralState({
        valid: false,
        text: err.response?.data?.error || 'This referral code is not valid.',
      });
    } finally {
      setReferralChecking(false);
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      toast.info('You need to be logged in to purchase.');
      navigate('/login');
      return;
    }
    if (!selectedPlan) return;

    setPurchasing(true);
    try {
      const { data } = await api.post(`/products/${id}/purchase`, {
        planId: selectedPlan.id,
        paymentMethod,
        // A `referralAllowed` itt is ott van, nem csak a mező megjelenítésénél:
        // a kódot semmilyen úton ne küldjük el olyan csomagra, amire nem jár.
        referralCode: referralAllowed && referralState?.valid
          ? referralCode.trim().toUpperCase()
          : undefined,
      });

      if (paymentMethod === 'crypto') {
        setCryptoOrder({
          ...data.order,
          ltcAddress: data.order.ltc_address,
          ltcAmount: data.order.ltc_amount,
          finalPrice: data.order.final_price,
          plan: { label: selectedPlan.label },
        });
      } else {
        if (!data.whopSessionId) {
          toast.error('Card payment is temporarily unavailable. Please use crypto.');
          return;
        }
        setCardOrder({ order: data.order, sessionId: data.whopSessionId });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Something went wrong.');
    } finally {
      setPurchasing(false);
    }
  };

  // A díj mértéke a szerverről jön (Owner Panel → Products), és a kerekítésnek
  // KARAKTERRE egyeznie kell a backend calcFinalPrice-ával — különben a kiírt ár
  // és a ténylegesen mentett final_price centekben elcsúszhat.
  const cardFeePercent = product.cardFeePercent ?? 0;
  const cardFeeLabel = Math.round(cardFeePercent * 100);

  const cardFeeFor = (base) => Math.ceil(base * cardFeePercent * 100) / 100;

  const priceFor = (plan) => {
    const base = parseFloat(plan.price);
    if (paymentMethod !== 'card') return base;
    return Math.round((base + cardFeeFor(base)) * 100) / 100;
  };

  return (
    <div className="page-container max-w-6xl">
      {cardOrder && (
        <CardPaymentModal
          order={cardOrder.order}
          sessionId={cardOrder.sessionId}
          planLabel={selectedPlan?.label}
          onClose={() => setCardOrder(null)}
        />
      )}
      {cryptoOrder && (
        <CryptoPaymentModal
          order={cryptoOrder}
          onClose={() => setCryptoOrder(null)}
          onExpire={() => {
            toast.error('Payment expired. Please try again.');
            setCryptoOrder(null);
          }}
        />
      )}

      {/* Morzsa */}
      <nav className="flex items-center gap-1.5 text-sm text-gray-500 mb-8">
        <Link to="/" className="hover:text-gray-300 transition-colors">Home</Link>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
             strokeLinecap="round" className="w-3.5 h-3.5 text-gray-700">
          <path d="M9 18l6-6-6-6" />
        </svg>
        <span className="text-gray-300 truncate">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-[1fr_26rem] gap-10 items-start">

        {/* ---------- Bal oldal: termék ---------- */}
        <div>
          <div className="relative w-full aspect-[16/10] rounded-xl border border-white/[0.07]
                          bg-black/30 mb-8" style={{ overflow: 'clip' }}>
            {product.image_url ? (
              <>
                <img src={product.image_url} alt={product.name}
                     className="absolute inset-0 w-full h-full object-contain" />
                <span className="absolute inset-0 bg-gradient-to-t from-gray-950/50 via-transparent to-transparent pointer-events-none" />
              </>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25"
                   strokeLinecap="round" strokeLinejoin="round"
                   className="absolute inset-0 m-auto w-16 h-16 text-gray-800">
                <path d="M14.5 3.5l6 6-9 9-3 1 1-3 9-9-2-2-9 9-1 3" />
                <path d="M3 21l3-3" />
              </svg>
            )}
          </div>

          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight">
            {product.name}
          </h1>
          <p className="text-gray-400 leading-relaxed max-w-2xl">{product.description}</p>

          <div className="mt-8 flex flex-wrap gap-2.5">
            {INCLUDED.map(f => (
              <span key={f} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg
                                       bg-white/[0.03] border border-white/[0.07] text-xs text-gray-300">
                <CheckIcon className="w-3.5 h-3.5 text-brand-400" />
                {f}
              </span>
            ))}
          </div>

          {/* Funkciólista */}
          <div className="mt-14">
            <h2 className="font-display text-xl font-semibold text-white mb-5 tracking-tight">
              What's inside
            </h2>
            <div className="space-y-2.5">
              {FEATURES.map(({ category, items }) => {
                const isOpen = openFeature === category;
                return (
                  <div
                    key={category}
                    className={`card overflow-hidden transition-colors duration-300 ${
                      isOpen ? 'border-brand-500/30' : 'hover:border-white/[0.12]'
                    }`}
                  >
                    <button
                      onClick={() => setOpenFeature(isOpen ? null : category)}
                      aria-expanded={isOpen}
                      className="w-full flex items-center gap-3.5 px-5 py-4 text-left"
                    >
                      <span className={`flex-shrink-0 w-8 h-8 rounded-lg border flex items-center justify-center
                                        transition-colors duration-300 ${
                        isOpen
                          ? 'bg-brand-500/15 border-brand-400/25 text-brand-200'
                          : 'bg-white/[0.03] border-white/[0.07] text-gray-500'
                      }`}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
                             strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                          {CATEGORY_ICONS[category]}
                        </svg>
                      </span>

                      <span className="flex-1 font-medium text-white">{category}</span>

                      <span className="text-xs text-gray-600 tabular-nums mr-1">{items.length}</span>
                      <svg
                        className={`w-4 h-4 text-gray-500 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" viewBox="0 0 24 24"
                      >
                        <path d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    <div className={`grid transition-all duration-300 ease-smooth ${
                      isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                    }`}>
                      <div className="overflow-hidden">
                        <div className="px-5 pb-5 pt-1 space-y-3">
                          {items.map(item => {
                            const parts = item.split(' — ');
                            const hasTitle = parts.length >= 2;
                            return (
                              <div key={item} className="flex items-start gap-3 text-sm">
                                <CheckIcon className="w-3.5 h-3.5 text-brand-400 flex-shrink-0 mt-1" />
                                {hasTitle ? (
                                  <div>
                                    <span className="text-white font-medium">{parts[0]}</span>
                                    <div className="mt-0.5 space-y-0.5">
                                      {parts.slice(1).map((p, i) => (
                                        <div key={i} className="text-gray-500 leading-relaxed">{p}</div>
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-gray-400 leading-relaxed">{item}</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ---------- Jobb oldal: vásárlás ---------- */}
        <div className="lg:sticky lg:top-24">
          <div className="card p-6 glow-border">
            <h2 className="font-display text-lg font-semibold text-white mb-5">Select a plan</h2>

            {isUpdating && (
              <div className="mb-5">
                <WarningBox>
                  Purchases are temporarily disabled while the service is being updated.
                </WarningBox>
              </div>
            )}

            {/* Fizetési mód */}
            <div className="grid grid-cols-2 gap-2 mb-5 p-1 rounded-xl bg-black/25 border border-white/[0.07]">
              {PAYMENT_METHODS.map(method => {
                const active = paymentMethod === method.id;
                return (
                  <button
                    key={method.id}
                    onClick={() => !isUpdating && setPaymentMethod(method.id)}
                    disabled={isUpdating}
                    className={`flex flex-col items-center justify-center gap-0.5 py-2.5 rounded-lg
                                text-sm font-medium transition-all duration-200 ease-smooth
                                disabled:opacity-45 disabled:cursor-not-allowed ${
                      active
                        ? 'bg-brand-gradient text-white border border-brand-400/25 shadow-glow-sm'
                        : 'text-gray-400 hover:text-white hover:bg-white/[0.05] border border-transparent'
                    }`}
                  >
                    <span className="flex items-center gap-2">{method.icon}{method.label}</span>
                    <span className={`text-[10px] ${active ? 'text-white/70' : 'text-gray-600'}`}>
                      {method.hint}
                    </span>
                  </button>
                );
              })}
            </div>

            {paymentMethod === 'card' && cardFeePercent > 0 && !isUpdating && (
              <div className="mb-5">
                <WarningBox tone="yellow">
                  A {cardFeeLabel}% card processing fee is added to card payments.
                  Pay with crypto to get the base price.
                </WarningBox>
              </div>
            )}

            {/* Csomagok */}
            <div className="space-y-2 mb-6">
              {product.plans?.map(plan => {
                const active = selectedPlan?.id === plan.id;
                const lifetime = plan.days >= 36000;
                return (
                  <button
                    key={plan.id}
                    onClick={() => !isUpdating && selectPlan(plan)}
                    disabled={isUpdating}
                    className={`w-full flex items-center gap-3 p-3.5 rounded-lg border text-left
                                transition-all duration-200 ease-smooth
                                disabled:opacity-45 disabled:cursor-not-allowed ${
                      active
                        ? 'border-brand-500/50 bg-brand-500/10'
                        : 'border-white/[0.07] bg-white/[0.02] hover:border-white/[0.16] hover:bg-white/[0.04]'
                    }`}
                  >
                    {/* Rádiógomb */}
                    <span className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center
                                      transition-colors duration-200 ${
                      active ? 'border-brand-400' : 'border-white/20'
                    }`}>
                      {active && <span className="w-1.5 h-1.5 rounded-full bg-brand-300" />}
                    </span>

                    <span className="flex-1 min-w-0">
                      <span className="block font-medium text-white truncate">{plan.label}</span>
                      <span className="block text-xs text-gray-500 mt-0.5">
                        {lifetime ? 'Unlimited' : `${plan.days} ${plan.days === 1 ? 'day' : 'days'}`}
                      </span>
                    </span>

                    <span className="text-right flex-shrink-0">
                      <span className={`block text-lg font-bold tabular-nums ${
                        active && !isUpdating ? 'text-brand-200' : 'text-white'
                      }`}>
                        €{plan.price}
                      </span>
                      {!lifetime && plan.days > 0 && (
                        <span className="block text-[11px] text-gray-600 tabular-nums">
                          €{(plan.price / plan.days).toFixed(2)}/day
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Ajánlói kód — csak arra a csomagra, amire tényleg jár jutalom.
                A rövid csomagnál a mező HELYETT egy mondat áll: a mező néma
                eltűnése úgy nézne ki, mintha a rendszer romlott volna el. */}
            {user && !isUpdating && product.referralEnabled !== false && !referralAllowed && (
              <p className="mb-6 text-xs text-gray-600 leading-relaxed">
                Referral codes apply to plans longer than {product.referralRewardDays ?? 7} days.
              </p>
            )}

            {user && !isUpdating && product.referralEnabled !== false && referralAllowed && (
              <div className="mb-6">
                <label className="label">
                  Referral code <span className="text-gray-600 normal-case tracking-normal">(optional)</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="input font-mono tracking-widest uppercase"
                    placeholder="XXXXXXXX"
                    value={referralCode}
                    maxLength={16}
                    onChange={e => { setReferralCode(e.target.value.toUpperCase()); setReferralState(null); }}
                    onBlur={checkReferral}
                  />
                  <button
                    type="button"
                    onClick={checkReferral}
                    disabled={referralChecking || !referralCode.trim()}
                    className="btn-secondary px-4 whitespace-nowrap"
                  >
                    {referralChecking ? '…' : 'Apply'}
                  </button>
                </div>
                {referralState && (
                  <p className={`text-xs mt-2 leading-relaxed ${
                    referralState.valid ? 'text-emerald-400' : 'text-red-400'
                  }`}>
                    {referralState.text}
                  </p>
                )}
              </div>
            )}

            {/* Összegzés */}
            {selectedPlan && !isUpdating && (() => {
              const basePrice = parseFloat(selectedPlan.price);
              const cardFee = cardFeeFor(basePrice);
              const finalPrice = priceFor(selectedPlan);
              return (
                <div className="surface-sunken p-4 mb-6 text-sm space-y-2.5">
                  <div className="flex justify-between text-gray-500">
                    <span>Plan</span><span className="text-gray-200">{selectedPlan.label}</span>
                  </div>
                  <div className="flex justify-between text-gray-500">
                    <span>Duration</span>
                    <span className="text-gray-200">
                      {selectedPlan.days >= 36000
                        ? 'Lifetime'
                        : `${selectedPlan.days} ${selectedPlan.days === 1 ? 'day' : 'days'}`}
                    </span>
                  </div>
                  {paymentMethod === 'card' && cardFeePercent > 0 && (
                    <div className="flex justify-between text-gray-500">
                      <span>Processing fee ({cardFeeLabel}%)</span>
                      <span className="text-amber-400 tabular-nums">+€{cardFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline pt-2.5 border-t border-white/[0.07]">
                    <span className="font-medium text-white">Total</span>
                    <span className="font-display font-bold text-xl text-brand-200 tabular-nums">
                      €{finalPrice}
                    </span>
                  </div>
                </div>
              );
            })()}

            <button
              onClick={handlePurchase}
              className="btn-primary w-full py-3 text-base"
              disabled={isUpdating || purchasing || !selectedPlan}
            >
              {isUpdating
                ? 'Purchases Disabled'
                : purchasing
                  ? 'Processing…'
                  : selectedPlan
                    ? `Purchase — €${priceFor(selectedPlan)}`
                    : 'Purchase — €—'}
            </button>

            {!isUpdating && (
              <p className="text-center text-xs text-gray-600 mt-3.5 leading-relaxed">
                After purchase you will receive a key to activate in your dashboard.
              </p>
            )}
          </div>

          <p className="text-center text-xs text-gray-600 mt-4">
            By purchasing you accept the{' '}
            <Link to="/terms" className="link">Terms of Service</Link>, including the no-refund policy.
          </p>
        </div>
      </div>
    </div>
  );
}
